// useOpenAIRealtimeCall
//
// The OpenAI Realtime voice-stack hook behind InterviewPage.
//
// Transport: worker-relayed WebSocket (/api/realtime/ws) — see
// OpenAIRealtimeService for why WebRTC was abandoned. callId is
// SERVER-issued: the relay's registry id, delivered via
// dd.relay.ready and returned from start(). Mid-call socket loss is
// handled inside the service (resume loop against the same relay
// call); a failed START is surfaced and the user clicks Start again.
//
// Session health (LUM-82): every capture chunk's level feeds a 15s
// bucketed breadcrumb, the "we can't hear you" detector, and any
// subscribed level indicator. Levels are numbers; audio never leaves
// the service.

import { useCallback, useEffect, useRef, useState } from 'react';
import { track as breadcrumb } from '@/lib/telemetry/breadcrumb';
import { MicLevelSampler, MicSilenceDetector } from '@/services/mic-level';
import {
  OpenAIRealtimeService,
  type RealtimeAgentState,
  type RealtimeCallEndData,
  type RealtimeCallStartData,
  type RealtimeConnectionState,
  type RealtimeErrorData,
  type RealtimeTranscript,
} from '@/services/OpenAIRealtimeService';
import { recordMarkedTopic } from '@/services/realtime-mark-topic';
import { logger } from '@/utils/logger';

export interface Transcript {
  role: string;
  text: string;
  timestamp: Date;
}

export interface CallEndFinalizeData {
  durationSeconds: number;
  transcripts: Transcript[];
}

export type MicLevelListener = (level: number) => void;

export interface UseOpenAIRealtimeCallOptions {
  onCallStart?: (callId: string | undefined) => void;
  onCallEnd?: (
    reason: string | undefined,
    finalize: CallEndFinalizeData,
  ) => void;
  onError?: (error: RealtimeErrorData, userMessage: string) => void;
  onAgentState?: (state: RealtimeAgentState) => void;
}

export interface UseOpenAIRealtimeCallReturn {
  ready: boolean;
  callActive: boolean;
  connecting: boolean;
  callId: string | null;
  transcripts: Transcript[];
  // Presence signal for the orb (LUM-133): listening = user's turn,
  // thinking = reply committed but not yet audible, speaking = audio out.
  agentState: RealtimeAgentState;
  /** Outline topics the interviewer marked COMPLETE this call (LUM-134),
   *  in mark order; only ever grows within a call. Empty when the
   *  mark_topic tool is off — the outline then stays static. */
  markedTopicIds: readonly string[];
  /** The interviewer has been waiting for the respondent and the mic
   *  has stayed flat for 15s (LUM-82). */
  micSilent: boolean;
  /** The service is inside its mid-call resume window. */
  reconnecting: boolean;
  /** Live per-chunk mic levels for an indicator; returns unsubscribe. */
  subscribeMicLevel: (listener: MicLevelListener) => () => void;
  /** Start a call bound to the interview session; resolves with the
   *  relay's call id. */
  startCall: (sessionId: string) => Promise<string | null>;
  endCall: () => Promise<void>;
  setInputDevice: (deviceId: string) => void;
  setOutputDevice: (deviceId: string) => void;
  clearTranscripts: () => void;
}

const FRIENDLY_ERROR =
  'Voice connection error. Please try again. If this persists, contact dialogue-help@luminarium.ai';

export function useOpenAIRealtimeCall(
  options: UseOpenAIRealtimeCallOptions = {},
): UseOpenAIRealtimeCallReturn {
  const { onCallStart, onCallEnd, onError, onAgentState } = options;

  const serviceRef = useRef<OpenAIRealtimeService | null>(null);
  const callIdRef = useRef<string | null>(null);
  const startedAtRef = useRef<number | null>(null);
  // Mirror of `transcripts` state — read in handleCallEnd to avoid the
  // stale-closure problem when persisting at end-of-call.
  const transcriptsRef = useRef<Transcript[]>([]);

  const [ready, setReady] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [callId, setCallId] = useState<string | null>(null);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [markedTopicIds, setMarkedTopicIds] = useState<readonly string[]>([]);
  const [agentState, setAgentState] = useState<RealtimeAgentState>('listening');
  const [micSilent, setMicSilent] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const micLevelListenersRef = useRef(new Set<MicLevelListener>());
  const samplerRef = useRef<MicLevelSampler | null>(null);
  const silenceRef = useRef(new MicSilenceDetector());

  // Stash callbacks in refs so the service init effect doesn't re-run when
  // the parent re-renders.
  const onCallStartRef = useRef(onCallStart);
  const onCallEndRef = useRef(onCallEnd);
  const onErrorRef = useRef(onError);
  const onAgentStateRef = useRef(onAgentState);
  useEffect(() => {
    onCallStartRef.current = onCallStart;
    onCallEndRef.current = onCallEnd;
    onErrorRef.current = onError;
    onAgentStateRef.current = onAgentState;
  }, [onCallStart, onCallEnd, onError, onAgentState]);

  const handleCallStart = useCallback((data: RealtimeCallStartData) => {
    logger.debug('useOpenAIRealtimeCall: call started', {
      callId: data.callId,
    });
    setCallActive(true);
    setConnecting(false);
    if (data.callId) {
      setCallId(data.callId);
      callIdRef.current = data.callId;
    }
    startedAtRef.current = Date.now();
    samplerRef.current?.stop();
    samplerRef.current = new MicLevelSampler(sample => {
      void breadcrumb('interview_audio_level_sample', {
        call_id: callIdRef.current,
        level_bucket: sample.levelBucket,
        peak_level_bucket: sample.peakBucket,
        frame_count: sample.frameCount,
      });
    });
    samplerRef.current.start();
    void breadcrumb('call_start', { call_id: data.callId ?? null });
    onCallStartRef.current?.(data.callId);
  }, []);

  const handleCallEnd = useCallback((data: RealtimeCallEndData) => {
    logger.debug('useOpenAIRealtimeCall: call ended', { reason: data.reason });
    setCallActive(false);
    setConnecting(false);
    // Back to the resting presence state — the orb must not keep
    // "speaking"/"thinking" once the call is over (LUM-133).
    setAgentState('listening');
    setReconnecting(false);
    samplerRef.current?.stop();
    samplerRef.current = null;
    const durationSeconds = startedAtRef.current
      ? Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000))
      : 0;
    startedAtRef.current = null;
    void breadcrumb('call_end', {
      call_id: callIdRef.current,
      end_reason: data.reason ?? null,
    });
    onCallEndRef.current?.(data.reason, {
      durationSeconds,
      transcripts: transcriptsRef.current,
    });
  }, []);

  const handleTranscript = useCallback((t: RealtimeTranscript) => {
    setTranscripts(prev => {
      const next = [
        ...prev,
        { role: t.role, text: t.text, timestamp: t.timestamp },
      ];
      transcriptsRef.current = next;
      return next;
    });
  }, []);

  const handleError = useCallback((error: RealtimeErrorData) => {
    logger.error('useOpenAIRealtimeCall: error', { error });
    void breadcrumb('call_error', {
      call_id: callIdRef.current,
      error_type: error.error?.type || 'unknown',
    });
    setConnecting(false);
    onErrorRef.current?.(error, FRIENDLY_ERROR);
  }, []);

  const handleAgentState = useCallback((state: RealtimeAgentState) => {
    setAgentState(state);
    onAgentStateRef.current?.(state);
  }, []);

  const handleTopicMarked = useCallback((topicId: string) => {
    setMarkedTopicIds(prev => recordMarkedTopic(prev, topicId));
  }, []);

  const handleMicLevel = useCallback((level: number) => {
    samplerRef.current?.push(level);
    silenceRef.current.onLevel(level, Date.now());
    for (const listener of micLevelListenersRef.current) listener(level);
  }, []);

  const handleConnectionState = useCallback(
    (state: RealtimeConnectionState) => {
      setReconnecting(state === 'reconnecting');
    },
    [],
  );

  // Stable refs for the service callbacks so we don't re-init the service
  // when these handler closures get recreated.
  const handlersRef = useRef({
    handleCallStart,
    handleCallEnd,
    handleTranscript,
    handleError,
    handleAgentState,
    handleTopicMarked,
    handleMicLevel,
    handleConnectionState,
  });
  useEffect(() => {
    handlersRef.current = {
      handleCallStart,
      handleCallEnd,
      handleTranscript,
      handleError,
      handleAgentState,
      handleTopicMarked,
      handleMicLevel,
      handleConnectionState,
    };
  }, [
    handleCallStart,
    handleCallEnd,
    handleTranscript,
    handleError,
    handleAgentState,
    handleTopicMarked,
    handleMicLevel,
    handleConnectionState,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const service = new OpenAIRealtimeService();
    service.initialize({
      onCallStart: data => handlersRef.current.handleCallStart(data),
      onCallEnd: data => handlersRef.current.handleCallEnd(data),
      onTranscript: t => handlersRef.current.handleTranscript(t),
      onError: e => handlersRef.current.handleError(e),
      onAgentState: s => handlersRef.current.handleAgentState(s),
      onTopicMarked: id => handlersRef.current.handleTopicMarked(id),
      onMicLevel: level => handlersRef.current.handleMicLevel(level),
      onConnectionState: s => handlersRef.current.handleConnectionState(s),
    });
    serviceRef.current = service;
    setReady(true);

    return () => {
      service.cleanup();
      serviceRef.current = null;
      samplerRef.current?.stop();
      samplerRef.current = null;
      setReady(false);
    };
  }, []);

  // "We can't hear you" (LUM-82): only while the interviewer is
  // waiting on the respondent — the presence state is the user's turn
  // — and the link is up. Polled because a dead capture path stops
  // delivering levels altogether.
  useEffect(() => {
    const detector = silenceRef.current;
    const awaitingUser =
      callActive && agentState === 'listening' && !reconnecting;
    detector.setAwaiting(awaitingUser, Date.now());
    setMicSilent(false);
    if (!awaitingUser) return;
    const id = window.setInterval(
      () => setMicSilent(detector.isFlat(Date.now())),
      1000,
    );
    return () => clearInterval(id);
  }, [callActive, agentState, reconnecting]);

  const subscribeMicLevel = useCallback((listener: MicLevelListener) => {
    const listeners = micLevelListenersRef.current;
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const startCall = useCallback(
    // sessionId is forwarded so the worker can register the call and
    // boot the relay observer (LUM-168); the worker owns the session
    // config.
    async (sessionId: string): Promise<string | null> => {
      if (!serviceRef.current) {
        logger.error('useOpenAIRealtimeCall: cannot start, service not ready');
        return null;
      }
      setConnecting(true);
      setCallActive(false);
      setCallId(null);
      callIdRef.current = null;
      // Fresh call → fresh outline; a mid-call resume never passes here.
      setMarkedTopicIds([]);

      try {
        const result = await serviceRef.current.start(sessionId);
        if (result.id) {
          callIdRef.current = result.id;
          setCallId(result.id);
        }
        return result.id ?? null;
      } catch (err) {
        logger.error('useOpenAIRealtimeCall: failed to start', { err });
        setConnecting(false);
        throw err;
      }
    },
    [],
  );

  const endCall = useCallback(async (): Promise<void> => {
    if (!serviceRef.current) return;
    try {
      await serviceRef.current.stop();
    } catch (err) {
      logger.error('useOpenAIRealtimeCall: error ending call', { err });
    } finally {
      setCallActive(false);
      setConnecting(false);
      setAgentState('listening');
    }
  }, []);

  const setInputDevice = useCallback((deviceId: string) => {
    serviceRef.current?.setInputDevice(deviceId);
  }, []);

  const setOutputDevice = useCallback((deviceId: string) => {
    serviceRef.current?.setOutputDevice(deviceId);
  }, []);

  const clearTranscripts = useCallback(() => {
    setTranscripts([]);
    transcriptsRef.current = [];
  }, []);

  return {
    ready,
    callActive,
    connecting,
    callId,
    transcripts,
    agentState,
    markedTopicIds,
    micSilent,
    reconnecting,
    subscribeMicLevel,
    startCall,
    endCall,
    setInputDevice,
    setOutputDevice,
    clearTranscripts,
  };
}

export default useOpenAIRealtimeCall;
